/**
 * The id the `gradient-brand-purple-stroke` utility points its `stroke` at.
 *
 * Hardcoded as `url(#gradient-brand-purple)` in `index.css`, so the two have to
 * agree — the constant is here to name that coupling, not to let it vary.
 */
const GRADIENT_ID = "gradient-brand-purple";

/**
 * The SVG gradient an icon's stroke resolves to inside a gradient Button.
 *
 * `gradientOutline` and `gradient` paint their label by making the text
 * transparent and clipping a background gradient to it. That trick reaches text
 * and nothing else, so an icon alongside the label inherits `currentColor` —
 * transparent — and disappears. `gradient-brand-purple-stroke` gives the icon
 * this gradient instead, which needs the definition to exist somewhere in the
 * document.
 *
 * Each component that draws a gradient icon renders its own, so several can be
 * mounted at once — a thread listing more than one artifact card does exactly
 * that. The duplicate ids are invalid but inert: every definition is
 * byte-for-byte the same, so whichever one a reference resolves to paints the
 * same gradient.
 *
 * `userSpaceOnUse`, not the default `objectBoundingBox`, and this is load-bearing
 * rather than a preference. Under object-bounding-box units the gradient is
 * mapped to each painted path's own box, and SVG does not paint an element whose
 * box encloses no area — so a perfectly horizontal or vertical path silently
 * disappears. Several lucide glyphs are built from exactly those: the three
 * interior rules of `FileText` are flat lines, and they rendered as nothing at
 * rest while the outline around them drew fine. Mapping to user space instead
 * gives every path in the icon one gradient and no degenerate boxes.
 *
 * The coordinates are the 24x24 viewBox every lucide icon uses, so a consumer
 * drawing at some other scale would need its own definition. The diagonal is
 * not what the earlier `rotate(139)` drew: that rotated the default horizontal
 * vector down and to the *left*, about the bounding box's origin, which under
 * object-bounding-box units left most of the box past the final stop and close
 * to flat. Down-and-right agrees with the `139deg` CSS gradients and with
 * `bg-gradient-to-br`, so the icons here now sweep the way the rest of the
 * brand treatment does.
 *
 * Zero-sized and absolutely positioned, so it is never a flex or grid item and
 * can sit anywhere among its siblings.
 */
export const GradientDefinition = () => (
  <svg width="0" height="0" className="absolute" aria-hidden="true">
    <defs>
      <linearGradient id={GRADIENT_ID} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="24" y2="24">
        <stop offset="12.97%" stopColor="#5f79f5" />
        <stop offset="74.93%" stopColor="#c084fc" />
      </linearGradient>
    </defs>
  </svg>
);
